import { Listbox, Transition } from "@headlessui/react";
import type { ReactElement, ReactNode } from "react";
import { useState, useId, useCallback, useEffect } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

type SelectOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

type SelectProps<T extends SelectOption> = {
  name: string;
  onChange?: (value: T) => void;
  options: T[];
  defaultValue?: T;
  selectedTemplate?: (selected: T) => ReactNode | ReactElement;
  optionTemplate?: (item: T, isSelected: boolean) => ReactNode | ReactElement;
};

export function SelectFilled<T extends SelectOption>(props: SelectProps<T>) {
  const { options, name, defaultValue, selectedTemplate, optionTemplate = defaultOptionTemplate, onChange } = props;

  const { t } = useTranslation();

  const id = useId();
  const [selected, setSelected] = useState<T>(
    defaultValue ||
      ({
        label: "",
        value: "",
      } as T)
  );

  useEffect(() => {
    if (defaultValue) setSelected(defaultValue as T);
  }, [defaultValue]);

  const handleChange = useCallback(
    (value: T) => {
      setSelected(value);
      setTimeout(() => onChange?.(value));
    },
    [setSelected, onChange]
  );

  return (
    <Listbox
      as="div"
      className="relative w-full cursor-pointer text-sm font-semibold text-primary"
      value={selected}
      onChange={handleChange}
    >
      {({ open, value }) => (
        <>
          <Listbox.Button className="flex min-h-[36px] w-full min-w-[36px] items-center bg-secondary-100 p-2 focus:outline-none">
            {selectedTemplate ? (
              selectedTemplate(selected)
            ) : (
              <span className="max-w-[250px] truncate">{value?.label ? selected?.label : t("common.not_selected")}</span>
            )}
          </Listbox.Button>
          <Transition
            className={clsx("absolute z-20 w-full")}
            enter="transform transition ease-in-out duration-300"
            enterFrom="-translate-y-1/2 opacity-0"
            enterTo="translate-y-0 opacity-1"
            leave="transform transition ease-out duration-200"
            leaveFrom="translate-y-0 opacity-1"
            leaveTo="-translate-y-1/2 opacity-0"
          >
            <Listbox.Options className="mt-0.5 flex w-full flex-col space-y-0.5">
              {options.map((option, idx) => (
                <Listbox.Option
                  key={idx}
                  value={option}
                  disabled={option?.disabled}
                  className="flex min-h-[36px] items-center bg-secondary-100 p-2"
                >
                  {optionTemplate(option, selected === option)}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
          <input type="hidden" id={id} name={name} value={selected?.value} />
        </>
      )}
    </Listbox>
  );
}

function defaultOptionTemplate(option: { label: string; value: string }) {
  return <span className="max-w-[250px] truncate">{option.label}</span>;
}
